import React, { useMemo } from "react";
import {
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  Tooltip,
  IconButton,
  Chip
} from "@material-ui/core";
import {
  AttachMoney,
  LocalOffer,
  Warning,
  Info,
  Help
} from "@material-ui/icons";
import styled from "styled-components";
import TourCalculationService from "../../../../services/TourCalculationService";

const StyledPaper = styled(Paper)`
  padding: 24px;
  margin-bottom: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleText = styled.div`
  font-weight: 600;
  color: #1976d2;
  font-size: 1.25rem;
  line-height: 1.4;
`;

const BreakdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${props => props.theme && props.theme.palette ? props.theme.palette.divider : '#e0e0e0'};
  
  &:last-child {
    border-bottom: none;
  }
`;

const BreakdownValue = styled(Typography)`
  font-weight: 600;
  color: ${props => props.theme && props.theme.palette ? props.theme.palette.text.primary : '#000000'};
`;

const TotalSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: ${props => props.theme && props.theme.palette ? props.theme.palette.primary.light : '#e3f2fd'};
  border-radius: 4px;
  border: 2px solid ${props => props.theme && props.theme.palette ? props.theme.palette.primary.main : '#1976d2'};
`;

const ChargeSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: ${props => props.theme && props.theme.palette ? props.theme.palette.secondary.light : '#fce4ec'};
  border-radius: 4px;
  border: 2px solid ${props => props.theme && props.theme.palette ? props.theme.palette.secondary.main : '#f50057'};
`;

const Alert = styled.div`
  padding: 16px;
  border-radius: 4px;
  margin-top: 16px;
  
  ${props => props.severity === 'warning' && `
    background-color: ${props.theme && props.theme.palette ? props.theme.palette.warning.light : '#fff3e0'};
    color: ${props.theme && props.theme.palette ? props.theme.palette.warning.contrastText : '#ef6c00'};
    border: 1px solid ${props.theme && props.theme.palette ? props.theme.palette.warning.main : '#ff9800'};
  `}
  
  ${props => props.severity === 'info' && `
    background-color: ${props.theme && props.theme.palette ? props.theme.palette.info.light : '#e3f2fd'};
    color: ${props.theme && props.theme.palette ? props.theme.palette.info.contrastText : '#1565c0'};
    border: 1px solid ${props.theme && props.theme.palette ? props.theme.palette.info.main : '#2196f3'};
  `}
`;

const DividerStyled = styled(Divider)`
  margin: 16px 0;
`;

/**
 * Componente optimizado para mostrar el cálculo de precios
 */
const PricingCalculator = React.memo(({
  pricing,
  onAmountChange,
  disabled = false,
  warnings = [],
  info = [],
  tourData = null
}) => {
  // Memoizar el desglose de precios para evitar re-cálculos innecesarios
  const priceBreakdown = useMemo(() => {
    if (!pricing || !pricing.breakdown) return null;

    const breakdown = pricing.breakdown;
    const items = [];

    // Calcular días efectivos correctos si tenemos datos del tour
    let effectiveDays = breakdown.effectiveDays;
    if (tourData && tourData.booking && tourData.booking.tour) {
      effectiveDays = TourCalculationService.calculateEffectiveDays(
        tourData.booking.tour.effective_start_date,
        tourData.booking.tour.effective_end_date
      );
    }

    // Precio base
    if (breakdown.basePrice > 0) {
      items.push({
        label: "Tour Base Price",
        value: breakdown.basePrice,
        type: "base",
        days: null,
        total: breakdown.basePrice
      });
    }

    // Precio del pasajero
    if (breakdown.passengerPrice > 0) {
      items.push({
        label: "Passenger Price",
        value: breakdown.passengerPrice,
        type: "passenger",
        days: null,
        total: breakdown.passengerPrice
      });
    }

    // GPS - Corregir cálculo para usar días efectivos correctos
    if (breakdown.gpsPrice > 0) {
      const dailyGpsRate = breakdown.gpsPrice / breakdown.effectiveDays;
      const correctedGpsPrice = dailyGpsRate * effectiveDays;
      
      items.push({
        label: "GPS Device",
        value: dailyGpsRate,
        type: "gps",
        days: effectiveDays,
        total: correctedGpsPrice,
        originalTotal: breakdown.gpsPrice,
        needsCorrection: effectiveDays !== breakdown.effectiveDays
      });
    } else {
      // Si no hay GPS en breakdown, calcular basado en días efectivos
      items.push({
        label: "GPS Device",
        value: 10,
        type: "gps",
        days: effectiveDays,
        total: 10 * effectiveDays,
        originalTotal: 0,
        needsCorrection: false
      });
    }

    // Seguro VIP
    if (breakdown.vipInsurancePrice > 0) {
      const dailyInsuranceRate = breakdown.vipInsurancePrice / breakdown.effectiveDays;
      const correctedInsurancePrice = dailyInsuranceRate * effectiveDays;
      
      items.push({
        label: "VIP Insurance",
        value: dailyInsuranceRate,
        type: "insurance",
        days: effectiveDays,
        total: correctedInsurancePrice,
        originalTotal: breakdown.vipInsurancePrice,
        needsCorrection: effectiveDays !== breakdown.effectiveDays
      });
    }

    // Seguro de cancelación
    if (breakdown.cancelInsurancePrice > 0) {
      items.push({
        label: "Cancel Insurance",
        value: breakdown.cancelInsurancePrice,
        type: "cancel_insurance",
        days: null,
        total: breakdown.cancelInsurancePrice
      });
    }

    // Habitación individual
    if (breakdown.singlePrice > 0) {
      items.push({
        label: "Single Room",
        value: breakdown.singlePrice,
        type: "single",
        days: null,
        total: breakdown.singlePrice
      });
    }

    // Modelo específico
    if (breakdown.modelPrice > 0) {
      items.push({
        label: "Vehicle Model",
        value: breakdown.modelPrice,
        type: "model",
        days: null,
        total: breakdown.modelPrice
      });
    }

    return items;
  }, [pricing, tourData]);

  // Memoizar los descuentos
  const discounts = useMemo(() => {
    if (!pricing || !pricing.discounts) return [];

    const discountItems = [];

    if (pricing.discounts.rewards > 0) {
      discountItems.push({
        label: "Rewards",
        value: -pricing.discounts.rewards,
        type: "discount"
      });
    }

    if (pricing.discounts.discount > 0) {
      discountItems.push({
        label: "Discount",
        value: -pricing.discounts.discount,
        type: "discount"
      });
    }

    return discountItems;
  }, [pricing]);

  // Calcular total corregido
  const correctedTotal = useMemo(() => {
    if (!priceBreakdown) return (pricing && pricing.total) || 0;

    let total = 0;
    priceBreakdown.forEach(item => {
      if (item.needsCorrection) {
        total += item.total; // Usar el total corregido
      } else {
        total += item.total;
      }
    });

    // Aplicar descuentos
    discounts.forEach(discount => {
      total += discount.value;
    });

    return total;
  }, [priceBreakdown, discounts]);

  // Formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  if (!pricing) {
    return (
      <StyledPaper>
        <Typography variant="body2" color="textSecondary">
          Cargando información de precios...
        </Typography>
      </StyledPaper>
    );
  }

  return (
    <StyledPaper>
      <TitleContainer>
        <TitleText>
          Cálculo de Precios
        </TitleText>
        <Tooltip title="Desglose detallado de los precios del tour">
          <IconButton size="small">
            <Help fontSize="small" />
          </IconButton>
        </Tooltip>
      </TitleContainer>

      {/* Desglose de precios */}
      {priceBreakdown && priceBreakdown.length > 0 && (
        <div>
          <Typography variant="subtitle2" gutterBottom>
            Desglose de Precios:
          </Typography>
          
          {priceBreakdown.map((item, index) => (
            <BreakdownItem key={index}>
              <div style={{ flex: 1 }}>
                <Typography variant="body2">
                  {item.label}
                  {item.days && (
                    <span style={{ color: item.needsCorrection ? '#f57c00' : '#666' }}>
                      {" "}(€{item.value.toFixed(2)}/día × {item.days} días)
                    </span>
                  )}
                </Typography>
                {item.needsCorrection && (
                  <Typography variant="caption" style={{ color: '#f57c00' }}>
                    Corregido: {formatCurrency(item.originalTotal)} → {formatCurrency(item.total)}
                  </Typography>
                )}
              </div>
              <BreakdownValue variant="body2" style={{ 
                color: item.needsCorrection ? '#f57c00' : 'inherit',
                fontWeight: item.needsCorrection ? 'bold' : 'normal'
              }}>
                {formatCurrency(item.total)}
              </BreakdownValue>
            </BreakdownItem>
          ))}

          {/* Descuentos */}
          {discounts.length > 0 && (
            <React.Fragment>
              {discounts.map((discount, index) => (
                <BreakdownItem key={`discount-${index}`}>
                  <Typography variant="body2" color="textSecondary">
                    {discount.label}
                  </Typography>
                  <BreakdownValue variant="body2" color="error">
                    {formatCurrency(discount.value)}
                  </BreakdownValue>
                </BreakdownItem>
              ))}
            </React.Fragment>
          )}

          <DividerStyled />

          {/* Total */}
          <TotalSection>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" color="primary">
                  Total del Tour:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="h5" color="primary" style={{ fontWeight: 700 }}>
                  {formatCurrency(correctedTotal)}
                </Typography>
              </Grid>
            </Grid>
          </TotalSection>
        </div>
      )}

      {/* Valor a cargar */}
      <ChargeSection>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle1" color="secondary">
              Valor a Cargar:
            </Typography>
            <Typography variant="h6" color="secondary" style={{ fontWeight: 600 }}>
              {formatCurrency(correctedTotal)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
                          <Button
                variant="contained"
                color="secondary"
                startIcon={<AttachMoney />}
                disabled={disabled}
                fullWidth
                onClick={() => onAmountChange && onAmountChange(correctedTotal)}
              >
                Procesar Pago
              </Button>
          </Grid>
        </Grid>
      </ChargeSection>

      {/* Mostrar advertencias */}
      {warnings.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Advertencias:</strong>
          </Typography>
          {warnings.map((warning, index) => (
            <Typography key={index} variant="body2">
              • {warning}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Mostrar información */}
      {info.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Información:</strong>
          </Typography>
          {info.map((infoItem, index) => (
            <Typography key={index} variant="body2">
              • {infoItem}
            </Typography>
          ))}
        </Alert>
      )}

      <DividerStyled />
    </StyledPaper>
  );
});

PricingCalculator.displayName = 'PricingCalculator';

export default PricingCalculator; 


// WEBPACK FOOTER //
// ./src/components/tour/TourProcessing/components/PricingCalculator.js